import { useState } from 'react';
import { cn } from '@/utils/cn';
import Typography from '../Typography/Typography';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TabOption {
  /** Unique identifier for the tab — used as the value in onChange callbacks. */
  value: string;
  /** Display label rendered inside the tab. */
  label: string;
}

export interface TabsProps {
  /** The tab options to render. */
  options: TabOption[];
  /**
   * Controlled selected value.
   * When provided, the component is fully controlled — you must handle `onChange`
   * to update this value (e.g. via TanStack Router's `navigate`).
   */
  value?: string;
  /**
   * Default selected value for uncontrolled usage.
   * Ignored when `value` is provided.
   * Defaults to the first tab's value when omitted.
   */
  defaultValue?: string;
  /**
   * Called when the user selects a different tab.
   * Receives the `value` of the newly selected tab.
   */
  onChange?: (value: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Primer Tabs component — navigation bar only.
 *
 * Supports both controlled and uncontrolled usage:
 *
 * ```tsx
 * // Uncontrolled — component manages selected state internally
 * <Tabs options={tabs} defaultValue="active" onChange={handleChange} />
 *
 * // Controlled — consumer owns state (e.g. TanStack Router)
 * <Tabs options={tabs} value={activeTab} onChange={(val) => navigate({ search: { tab: val } })} />
 * ```
 *
 * The component renders a full-width tab bar. Each tab shares equal width
 * (`flex-1`). Content below the tabs is the consumer's responsibility.
 */
export function Tabs({ options, value, defaultValue, onChange }: TabsProps) {
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<string>(
    () => defaultValue ?? options[0]?.value ?? ''
  );

  const selectedValue = isControlled ? value : internalValue;

  function handleSelect(tabValue: string) {
    if (!isControlled) {
      setInternalValue(tabValue);
    }
    onChange?.(tabValue);
  }

  return (
    <div role="tablist" className="flex w-full bg-surface rounded-t-large">
      {options.map((option) => {
        const isSelected = option.value === selectedValue;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isSelected}
            onClick={() => handleSelect(option.value)}
            className={cn(
              // Layout — equal width, flex column so border sits flush at bottom
              'flex-1 flex flex-col items-center',
              'py-4 px-2',
              'rounded-t-large',
              // Bottom border indicator — always present, transparent when inactive
              // to avoid layout shift. 2px matches the visual weight in the design.
              'border-b-2',
              isSelected ? 'border-(--border-color-interactiveloud)' : 'border-transparent',
              // Cursor
              'cursor-pointer',
              // Remove default button chrome
              'bg-transparent appearance-none outline-none',
              // Focus ring for keyboard navigation
              'focus-visible:ring-2 focus-visible:ring-offset-1',
              'focus-visible:ring-(--border-color-interactiveloud)'
            )}
          >
            <Typography
              variant="header"
              size="h3"
              as="span"
              color={isSelected ? 'header' : 'disabled'}
            >
              {option.label}
            </Typography>
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
